import axios, { AxiosError, AxiosResponse, CancelTokenSource } from 'axios'
import AnonymousResources from './AnonymousResource'
import { IAbrAbn, IAbrMessage } from '../models/AbrMessage'
import { IFindMyAwardRequest } from '../models/Award'
import { IHeartbeatResponse } from '../models/HealthMessage'
import { ILocation } from '../models/Address'
import { IChoiceResponse } from '../models/Choice'
import {
  UnpaidWageSearchSubmitDetailsRequest,
  UnpaidWageSearchSubmitRequest,
  UnpaidWageSearchSubmitResponse,
} from '../models/UnpaidWages'
import {
  IInteractiveTemplateGetFormRequest,
  IInteractiveTemplateGetFormResponse,
  IInteractiveTemplatePostFormRequest,
} from '../models/InteractiveTemplate'
import {
  IQuestionAnswerResponse,
  IQuizAnswerRequest,
  IQuizResponse,
  IWorkplaceQuizResponse,
} from '../models/WorkplaceQuiz'
import { ICalloutResponse } from '../models/Callout'
import { Feedback, IFeedbackLookupData } from '../models/Feedback'
import { ApiResponse } from '../models/Common'

const anonymousAxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,

  headers: {
    'Access-Control-Allow-Origin': '*',
    'Access-Control-Allow-Methods': 'GET, POST, DELETE, OPTIONS',
    'Access-Control-Allow-Headers':
      'Content-Type, Access-Control-Allow-Headers',
  },
})

anonymousAxiosInstance.interceptors.request.use((config) => {
  return config
})

anonymousAxiosInstance.interceptors.response.use(
  (response) => {
    return response
  },
  (error: AxiosError) => {
    if (error.response) {
      const code = error.response.status
      switch (code) {
        case 400:
        case 404:
          const { status }: { status: number } = error.response.data
          return Promise.reject({ errors: error.response.data?.errors, status })
        case 500:
          return Promise.reject(error.message)
      }
      return Promise.reject(error.response)
    }

    if (error.message === 'Network Error') {
      window.location.href = '/network-error'
      return Promise.reject(
        'There are some connectivity issues. Please try again.'
      )
    }

    return Promise.reject(error.message)
  }
)

const responseBody = <T>(response: AxiosResponse<T>) => response.data

const requests = {
  post: <T>(url: string, body: null, source?: CancelTokenSource) =>
    anonymousAxiosInstance
      .post<T>(url, body, { cancelToken: source?.token })
      .then(responseBody),
  get: <T>(url: string, source?: CancelTokenSource) =>
    anonymousAxiosInstance
      .get<T>(url, { cancelToken: source?.token })
      .then(responseBody),
}

const Heartbeat = {
  getHeartbeat: () =>
    requests.get<ApiResponse<IHeartbeatResponse>>(
      `${AnonymousResources.HeartbeatEndpoints.Heartbeat}`
    ),
}
const AbrSearch = {
  getAbnDetails: (id: string) =>
    requests.get<ApiResponse<IAbrAbn>>(
      `${AnonymousResources.AbrEndpoints.GetAbnDetails}?id=${id}`
    ),
  getAbnMatchingNames: (name: string) =>
    requests.get<ApiResponse<IAbrMessage>>(
      `${AnonymousResources.AbrEndpoints.GetAbnMatchingNames}?name=${name}`
    ),
}

const Address = {
  getLocations: (query: string) =>
    requests.get<ApiResponse<ILocation[]>>(
      `${AnonymousResources.AddressEndpoints.GetLocations}?term=${query}`
    ),
}

const Awards = {
  findMyAward: (request: IFindMyAwardRequest) =>
    requests.post<ApiResponse<any>>(
      AnonymousResources.AwardsEndPoints.FindMyAward,
      request
    ),
}

const Choices = {
  getEntry: (source: CancelTokenSource) =>
    requests.get<ApiResponse<IChoiceResponse>>(
      AnonymousResources.ChoicesEndpoints.GetEntry,
      source
    ),
  getNextSelectionByChoice: (id: number, source: CancelTokenSource) =>
    requests.get<ApiResponse<IChoiceResponse>>(
      `${AnonymousResources.ChoicesEndpoints.GetNextSelectionByChoice}`.replace(
        '{id}',
        `${id}`
      ),
      source
    ),
  getPreviousSelectionByChoice: (id: number, source: CancelTokenSource) =>
    requests.get<ApiResponse<IChoiceResponse>>(
      `${AnonymousResources.ChoicesEndpoints.GetPreviousSelectionByChoice}`.replace(
        '{id}',
        `${id}`
      ),
      source
    ),
}

const UnpaidWages = {
  submit: (request: UnpaidWageSearchSubmitRequest, source: CancelTokenSource) =>
    requests.post<ApiResponse<UnpaidWageSearchSubmitResponse>>(
      `${AnonymousResources.UnpaidWagesEndpoints.Submit}`,
      request,
      source
    ),
  possibleMatch: (
    request: UnpaidWageSearchSubmitDetailsRequest,
    source: CancelTokenSource
  ) =>
    requests.post<ApiResponse<boolean>>(
      `${AnonymousResources.UnpaidWagesEndpoints.PossibleMatch}`,
      request,
      source
    ),
}

const InteractiveTemplate = {
  getForm: (request: IInteractiveTemplateGetFormRequest) =>
    requests.post<ApiResponse<IInteractiveTemplateGetFormResponse>>(
      AnonymousResources.InteractiveTemplateEndpoints.GetForm,
      request
    ),

  postForm: (request: IInteractiveTemplatePostFormRequest) =>
    anonymousAxiosInstance.post(
      AnonymousResources.InteractiveTemplateEndpoints.PostForm,
      request,
      { responseType: 'blob' }
    ),
}

const WorkplaceQuiz = {
  getModules: (source: CancelTokenSource) =>
    requests.get<ApiResponse<IWorkplaceQuizResponse>>(
      AnonymousResources.WorkplaceQuizEndpoints.GetWorkplaceQuiz,
      source
    ),
  getModule: (id: number, source: CancelTokenSource) =>
    requests.get<ApiResponse<IQuizResponse>>(
      `${AnonymousResources.WorkplaceQuizEndpoints.GetQuizById}`.replace(
        '{id}',
        `${id}`
      ),
      source
    ),
  checkAnswer: (request: IQuizAnswerRequest, source: CancelTokenSource) =>
    requests.post<ApiResponse<IQuestionAnswerResponse>>(
      AnonymousResources.WorkplaceQuizEndpoints.CheckAnswer,
      request,
      source
    ),
}

const Callout = {
  getCalloutByPage: (
    page: string,
    position: string,
    source: CancelTokenSource
  ) =>
    requests.get<ICalloutResponse>(
      `${AnonymousResources.CalloutEndpoints.GetCalloutByPage}`
        .replace('{page}', `${page}`)
        .replace('{position}', `${position}`),
      source
    ),
}

const GovCMS = {
  getFooterContentLinks: (source: CancelTokenSource) =>
    requests.get<ApiResponse<any>>(
      AnonymousResources.GovCMSEndpoints.GetFooterContents,
      source
    ),
  getHeaderContentLinks: (source: CancelTokenSource) =>
    requests.get<ApiResponse<any>>(
      AnonymousResources.GovCMSEndpoints.GetHeaderContents,
      source
    ),
}

const RefData = {
  getLookupData: (source: CancelTokenSource) =>
    requests.get<ApiResponse<IFeedbackLookupData>>(
      AnonymousResources.RefDataEndpoints.GetFeedbackLookupData,
      source
    ),
}

const Feedbacks = {
  create: (request: Feedback, source: CancelTokenSource) =>
    requests.post<ApiResponse<boolean>>(
      `${AnonymousResources.FeedbackEndpoints.CreateFeedback}`,
      request,
      source
    ),
}

const AnonymousAgent = {
  AbrSearch,
  Heartbeat,
  Address,
  Awards,
  Choices,
  UnpaidWages,
  InteractiveTemplate,
  WorkplaceQuiz,
  Callout,
  GovCMS,
  RefData,
  Feedbacks,
}

export default AnonymousAgent
